/**
 * AgentChildSvidPolicySlideout — Stage 3c-3 (P0-IDENT).
 *
 * Edit form for an agent's child SVID minting policy. Validates the
 * V200 invariant mirror: allowChildren=false requires maxChildren=0
 * AND empty allowedSpiffeIds. Backend rejects violations with a 400;
 * we catch the same cases client-side for a faster failure.
 *
 * Reached from ChildSvidPolicySection in IdentityAgentDetailPageV2.
 */
import React, { useEffect, useState } from 'react';
import { Save, X as XIcon, AlertTriangle, Trash2 } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Pill } from '../Pill';
import {
  updateIdentityAgentOverride,
  resetIdentityAgentOverride,
} from '../../../services/api';

export function AgentChildSvidPolicySlideout({
  isOpen,
  onClose,
  onSaved,
  agentId,
  agentName,
  mode = 'add',
  // Optional pre-fill from the existing override (when an admin
  // re-edits rather than creates fresh).
  initialAllowChildren = false,
  initialMaxChildren = 0,
  initialAllowedSpiffeIds = [],
  initialTrustBoundary = 'tenant-bounded',
}) {
  const [allowChildren, setAllowChildren] = useState(initialAllowChildren);
  const [maxChildren, setMaxChildren] = useState(String(initialMaxChildren || ''));
  const [allowedSpiffeIds, setAllowedSpiffeIds] = useState(
    (initialAllowedSpiffeIds || []).join('\n'));
  const [trustBoundary, setTrustBoundary] = useState(initialTrustBoundary);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) return;
    setAllowChildren(false); setMaxChildren(''); setAllowedSpiffeIds('');
    setTrustBoundary('tenant-bounded'); setError(null);
  }, [isOpen]);

  // When the slideout opens with new initial values (e.g. re-edit
  // of an existing override), sync state.
  useEffect(() => {
    if (!isOpen) return;
    setAllowChildren(initialAllowChildren);
    setMaxChildren(String(initialMaxChildren || ''));
    setAllowedSpiffeIds((initialAllowedSpiffeIds || []).join('\n'));
    setTrustBoundary(initialTrustBoundary);
  }, [isOpen, initialAllowChildren, initialMaxChildren, initialAllowedSpiffeIds, initialTrustBoundary]);

  const inputCls =
    'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]';

  const handleAdd = async () => {
    setError(null);

    const allowedIds = allowedSpiffeIds
      .split('\n').map(s => s.trim()).filter(Boolean);
    const maxKidsInt = maxChildren ? parseInt(maxChildren, 10) : 0;

    // Client-side invariant — mirrors V200 identity_agent_kid_bounds.
    if (!allowChildren && (maxKidsInt > 0 || allowedIds.length > 0)) {
      setError('When child minting is disabled, max children must be 0 and the allowlist must be empty.');
      return;
    }
    if (allowChildren) {
      const bad = allowedIds.find(s => !s.startsWith('spiffe://'));
      if (bad) {
        setError(`Allowed SPIFFE ID must start with spiffe://: ${bad}`);
        return;
      }
    }
    if (!['tenant-bounded', 'instance-bounded', 'org-bounded'].includes(trustBoundary)) {
      setError('Trust boundary must be one of tenant-bounded, instance-bounded, org-bounded.');
      return;
    }

    const body = {
      allowChildren,
      maxChildren: maxKidsInt,
      allowedChildSpiffeIds: allowedIds,
      trustBoundary,
    };

    setSaving(true);
    const res = await updateIdentityAgentOverride(agentId, body);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Save failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(res?.data);
    onClose();
  };

  const handleRemove = async () => {
    setError(null);
    setSaving(true);
    const res = await resetIdentityAgentOverride(agentId);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Delete failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(null);
    onClose();
  };

  const isRemove = mode === 'remove';

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      ariaLabel={isRemove ? 'Remove child SVID policy override' : 'Edit child SVID policy'}
      header={
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            {isRemove ? 'Remove child SVID policy' : 'Edit child SVID policy'}
          </h2>
          <Pill tone={isRemove ? 'danger' : (allowChildren ? 'warning' : 'trust')} size="sm">
            {isRemove ? 'Destructive' : 'Override'}
          </Pill>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" iconLeft={<XIcon size={13} />} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {isRemove ? (
            <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />}
                    onClick={handleRemove} disabled={saving}>
              {saving ? 'Removing…' : 'Remove override'}
            </Button>
          ) : (
            <Button variant="primary" size="sm" iconLeft={<Save size={13} />}
                    onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : 'Save policy'}
            </Button>
          )}
        </div>
      }
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 text-[12.5px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="text-[12.5px] text-v2-ink-secondary">
          Agent: <span className="text-v2-ink font-semibold">{agentName || agentId}</span>
        </div>

        {isRemove ? (
          <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-md px-3 py-2.5 text-[12.5px] text-v2-warning">
            Removing the override clears the agent's child SVID policy AND any
            rotation / federation overrides on the same row. The agent reverts
            to the safe default (no child minting).
          </div>
        ) : (
          <>
            <div className="bg-v2-info-soft border border-v2-border-subtle rounded-v2-md px-3 py-2.5 text-[12px] text-v2-ink-secondary">
              Child SVID minting is an <strong className="text-v2-ink">authority delegation</strong> —
              it lets this agent issue SVIDs for sub-agents under its control. Grant it
              only when the agent runs a known supervised workload pattern.
            </div>

            <div className="flex items-center gap-2.5">
              <input type="checkbox" id="allowChildren"
                     checked={allowChildren}
                     onChange={(e) => setAllowChildren(e.target.checked)} />
              <label htmlFor="allowChildren" className="text-[13px] text-v2-ink">
                Allow child SVID minting
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">Max children</label>
                <input type="number" min="0" value={maxChildren}
                       disabled={!allowChildren}
                       onChange={(e) => setMaxChildren(e.target.value)}
                       placeholder="0" className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">Trust boundary</label>
                <select value={trustBoundary}
                        onChange={(e) => setTrustBoundary(e.target.value)}
                        className={inputCls}>
                  <option value="tenant-bounded">tenant-bounded (safest)</option>
                  <option value="instance-bounded">instance-bounded</option>
                  <option value="org-bounded">org-bounded (broadest)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] text-v2-ink-secondary mb-1">
                Allowed child SPIFFE IDs (one per line)
              </label>
              <textarea value={allowedSpiffeIds}
                        disabled={!allowChildren}
                        onChange={(e) => setAllowedSpiffeIds(e.target.value)}
                        placeholder={"spiffe://acme.local/child/a\nspiffe://acme.local/child/b"}
                        rows={4}
                        className={inputCls} style={{ resize: 'vertical' }} />
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                Each line is a SPIFFE ID this agent may mint as a child. Leave empty
                to disallow all children even when minting is enabled.
              </div>
            </div>
          </>
        )}
      </div>
    </Slideout>
  );
}

export default AgentChildSvidPolicySlideout;
